import {
  HubspotCompanyEntity,
  HubspotContactEntity,
  HubspotDealEntity,
  HubspotPipelineEntity,
  HubspotPipelineStageEntity,
  HubspotProperty,
  HubspotPropertyEntity,
} from "@cohesive/api-entities";
import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { orgAPI } from "../api";

type HubSpotObjectType = "contacts" | "companies" | "deals";

// HubSpot is served by two interchangeable kits: the legacy Nango proxy kit
// (action names `hubspot-*`) and the remote MCP server (action names
// `hubspot-mcp-*`). A connection's `integration_provider` ("nango" | "mcp")
// decides which one backs it, so the same selector drives either. Only
// contacts/companies/deals search+get exist on the MCP side (read-only);
// owners/pipelines/properties remain Nango-only.
type HubSpotKit = "nango" | "mcp";

const hubspotKit = (provider?: string): HubSpotKit =>
  provider === "mcp" ? "mcp" : "nango";

const searchActionNames: Record<
  HubSpotKit,
  Record<HubSpotObjectType, string>
> = {
  nango: {
    contacts: "hubspot-contact-search",
    companies: "hubspot-company-search",
    deals: "hubspot-deal-search",
  },
  mcp: {
    contacts: "hubspot-mcp-contact-search",
    companies: "hubspot-mcp-company-search",
    deals: "hubspot-mcp-deal-search",
  },
};

const getActionNames: Record<HubSpotKit, Record<HubSpotObjectType, string>> = {
  nango: {
    contacts: "hubspot-contact-get",
    companies: "hubspot-company-get",
    deals: "hubspot-deal-get",
  },
  mcp: {
    contacts: "hubspot-mcp-contact-get",
    companies: "hubspot-mcp-company-get",
    deals: "hubspot-mcp-deal-get",
  },
};

interface HubSpotObjectParams {
  integrationConnectionId: string;
  id: string;
  objectType: HubSpotObjectType;
  // Provider of the connection ("nango" | "mcp"); selects which kit's action
  // names to call. Defaults to the Nango kit when omitted.
  provider?: string;
}

type HubSpotObjectEntityMap = {
  contacts: HubspotContactEntity;
  companies: HubspotCompanyEntity;
  deals: HubspotDealEntity;
};

export function useHubSpotObject<T extends keyof HubSpotObjectEntityMap>(
  params: HubSpotObjectParams,
  options?: Partial<
    UseQueryOptions<HubSpotObjectEntityMap[T] | null, AxiosError>
  >,
) {
  return useQuery<HubSpotObjectEntityMap[T] | null, AxiosError>({
    queryKey: [
      "hubspot-object-get",
      params.integrationConnectionId,
      params.provider,
      params.objectType,
      params.id,
    ],
    queryFn: async () => {
      const response = await orgAPI.toolbox.execute<HubSpotObjectEntityMap[T]>({
        integration_connection_id: params.integrationConnectionId,
        action_name:
          getActionNames[hubspotKit(params.provider)][params.objectType],
        action_input: { hs_object_id: params.id },
      });
      // The get action returns an array with one entity, return the first one
      return response.data;
    },
    ...options,
  });
}

interface HubSpotSearchParams {
  integrationConnectionId: string;
  query: string;
  objectType: HubSpotObjectType;
  // Provider of the connection ("nango" | "mcp"); selects which kit's action
  // names to call. Defaults to the Nango kit when omitted.
  provider?: string;
}

type HubSpotSearchEntityMap = {
  contacts: HubspotContactEntity;
  companies: HubspotCompanyEntity;
  deals: HubspotDealEntity;
};

export function useHubSpotSearch<T extends keyof HubSpotSearchEntityMap>(
  params: HubSpotSearchParams,
  options?: Partial<UseQueryOptions<HubSpotSearchEntityMap[T][], AxiosError>>,
) {
  return useQuery<HubSpotSearchEntityMap[T][], AxiosError>({
    queryKey: [
      "hubspot-search",
      params.integrationConnectionId,
      params.provider,
      params.objectType,
      params.query,
    ],
    queryFn: async () => {
      const response = await orgAPI.toolbox.execute<
        HubSpotSearchEntityMap[T][]
      >({
        integration_connection_id: params.integrationConnectionId,
        action_name:
          searchActionNames[hubspotKit(params.provider)][params.objectType],
        action_input: { query: params.query },
      });
      return response.data;
    },
    ...options,
  });
}

export function useHubSpotSearchInfinite<
  T extends keyof HubSpotSearchEntityMap,
>(
  params: HubSpotSearchParams,
  options?: Partial<
    UseInfiniteQueryOptions<
      HubSpotSearchEntityMap[T][],
      AxiosError,
      InfiniteData<HubSpotSearchEntityMap[T][]>,
      HubSpotSearchEntityMap[T][],
      unknown[],
      Record<string, unknown> | undefined
    >
  >,
) {
  return useInfiniteQuery({
    queryKey: [
      "hubspot-search-infinite",
      params.integrationConnectionId,
      params.provider,
      params.objectType,
      params.query,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await orgAPI.toolbox.execute<
        HubSpotSearchEntityMap[T][]
      >({
        integration_connection_id: params.integrationConnectionId,
        action_name:
          searchActionNames[hubspotKit(params.provider)][params.objectType],
        action_input: {
          query: params.query,
          ...pageParam,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      // If the last page has fewer results than expected, we're done
      if (lastPage.length < 100) return undefined;
      // Return pagination info for the next page
      return { after: allPages.flat().length.toString() };
    },
    initialPageParam: undefined as Record<string, unknown> | undefined,
    ...options,
  });
}

interface HubSpotOwnerParams {
  integrationConnectionId: string;
  id: string;
}

export function useHubSpotOwner(
  params: HubSpotOwnerParams,
  options?: Partial<UseQueryOptions<HubspotContactEntity | null, AxiosError>>,
) {
  return useQuery<HubspotContactEntity | null, AxiosError>({
    queryKey: ["hubspot-owner-get", params.integrationConnectionId, params.id],
    queryFn: async () => {
      const response = await orgAPI.toolbox.execute<HubspotContactEntity>({
        integration_connection_id: params.integrationConnectionId,
        action_name: "hubspot-owner-get",
        action_input: { owner_id: params.id },
      });
      return response.data;
    },
    ...options,
  });
}

interface HubSpotOwnersParams {
  integrationConnectionId: string;
}

export function useHubSpotOwners(
  params: HubSpotOwnersParams,
  options?: Partial<UseQueryOptions<HubspotContactEntity[], AxiosError>>,
) {
  return useQuery<HubspotContactEntity[], AxiosError>({
    queryKey: ["hubspot-owners-list", params.integrationConnectionId],
    queryFn: async () => {
      const response = await orgAPI.toolbox.execute<HubspotContactEntity[]>({
        integration_connection_id: params.integrationConnectionId,
        action_name: "hubspot-owners-list",
        action_input: {},
      });
      return response.data;
    },
    ...options,
  });
}

interface HubSpotPipelinesParams {
  integrationConnectionId: string;
  type: "deals" | "tickets";
}

export function useHubSpotPipelines(
  params: HubSpotPipelinesParams,
  options?: Partial<UseQueryOptions<HubspotPipelineEntity[], AxiosError>>,
) {
  return useQuery<HubspotPipelineEntity[], AxiosError>({
    queryKey: [
      "hubspot-pipeline-list",
      params.integrationConnectionId,
      params.type,
    ],
    queryFn: async () => {
      const response = await orgAPI.toolbox.execute<HubspotPipelineEntity[]>({
        integration_connection_id: params.integrationConnectionId,
        action_name: "hubspot-pipeline-list",
        action_input: { object_type: params.type },
      });
      return response.data;
    },
    ...options,
  });
}

interface HubSpotPipelineStagesParams {
  integrationConnectionId: string;
  objectType: "deals" | "tickets";
  pipelineId: string;
}

export function useHubSpotPipelineStages(
  params: HubSpotPipelineStagesParams,
  options?: Partial<UseQueryOptions<HubspotPipelineStageEntity[], AxiosError>>,
) {
  return useQuery<HubspotPipelineStageEntity[], AxiosError>({
    queryKey: [
      "hubspot-pipeline-stages-list",
      params.integrationConnectionId,
      params.objectType,
      params.pipelineId,
    ],
    queryFn: async () => {
      const response = await orgAPI.toolbox.execute<
        HubspotPipelineStageEntity[]
      >({
        integration_connection_id: params.integrationConnectionId,
        action_name: "hubspot-pipeline-stages-list",
        action_input: {
          object_type: params.objectType,
          pipeline_id: params.pipelineId,
        },
      });
      return response.data;
    },
    ...options,
  });
}

interface HubSpotPropertiesParams {
  integrationConnectionId: string;
  objectType: "companies" | "contacts" | "deals";
}

export function useHubSpotProperties(
  params: HubSpotPropertiesParams,
  options?: Partial<UseQueryOptions<HubspotProperty[], AxiosError>>,
) {
  return useQuery<HubspotProperty[], AxiosError>({
    queryKey: [
      "hubspot-properties-list",
      params.integrationConnectionId,
      params.objectType,
    ],
    queryFn: async () => {
      const response = await orgAPI.toolbox.execute<HubspotPropertyEntity[]>({
        integration_connection_id: params.integrationConnectionId,
        action_name: "hubspot-properties-list",
        action_input: { object_type: params.objectType },
      });
      // Extract the data field from each entity
      return response.data.map((entity) => entity.data);
    },
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes since properties rarely change
    ...options,
  });
}
